import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Hash,
  Home,
  KeyRound,
  Mail,
  Percent,
  Save,
  Shield,
  UserRound,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ManagerLayout from '../../components/manager/ManagerLayout';
import BuilderLayout from '../../components/builder/BuilderLayout';
import LoadingState, { Spinner } from '../../components/common/LoadingState';
import { ProfileField } from '../../components/profile/ProfileField';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { useAuth, getRoleDisplayName } from '../../context/AuthContext';
import { profileService, MyProfileUser } from '../../services/profileService';
import { notifyError, notifySuccess } from '../../utils/notificationCenter';
import { cn } from '../../utils/cn';

const getInitials = (firstName?: string, lastName?: string) => {
  const first = firstName?.trim().charAt(0) || '';
  const last = lastName?.trim().charAt(0) || '';
  return `${first}${last}`.toUpperCase() || 'U';
};

const ProfilePageContent: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<MyProfileUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFormKey, setPasswordFormKey] = useState(0);

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const data = await profileService.getMyProfile(user.id);
      setProfile(data);
      setFirstName(data.firstName || '');
      setLastName(data.lastName || '');
      setPhone(data.profile?.phone || '');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const isProfileDirty = useMemo(() => {
    if (!profile) return false;
    return (
      firstName.trim() !== (profile.firstName || '').trim() ||
      lastName.trim() !== (profile.lastName || '').trim() ||
      phone.trim() !== (profile.profile?.phone || '').trim()
    );
  }, [firstName, lastName, phone, profile]);

  const passwordsFilled = currentPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0;
  const isNewPasswordValid = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword;
  const showConfirmMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canChangePassword =
    currentPassword.length > 0 && isNewPasswordValid && passwordsMatch && confirmPassword.length > 0;

  const handleBack = () => {
    if (user?.role === 'admin') {
      navigate('/admin/assemblies');
      return;
    }
    if (user?.role === 'manager') {
      navigate('/manager/delegations');
      return;
    }
    if (user?.role === 'builder') {
      navigate('/builder/powers');
      return;
    }
    navigate('/dashboard');
  };

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.id || !isProfileDirty) return;

    setSaving(true);
    try {
      await profileService.updateMyBasicInfo(user.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      await profileService.updateMyPhone(user.id, phone.trim());
      await refreshUser();
      await loadProfile();
      notifySuccess('Perfil actualizado correctamente');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canChangePassword) return;

    setChangingPassword(true);
    try {
      await profileService.changePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordFormKey((current) => current + 1);
      notifySuccess('Contraseña actualizada correctamente');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Error al cambiar la contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <LoadingState layout="page" message="Cargando perfil..." />;
  }

  if (!profile || !user) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
        No se pudo cargar tu perfil.
      </div>
    );
  }

  const showResidentialBlock = ['owner', 'tenant'].includes(profile.role);
  const showManagerBlock = profile.role === 'manager';
  const showBuilderBlock = profile.role === 'builder';
  const fullName = `${profile.firstName} ${profile.lastName}`.trim();

  const residentialItems = [
    { label: 'Apartamento', value: profile.profile?.apartment || '—', icon: Home },
    { label: 'Conjunto', value: profile.profile?.complex || '—', icon: Building2 },
    {
      label: 'Coeficiente',
      value:
        profile.profile?.coefficient != null
          ? Number(profile.profile.coefficient).toLocaleString('es-CO', {
              maximumFractionDigits: 10,
            })
          : '—',
      icon: Percent,
    },
    { label: 'Documento', value: profile.profile?.document || '—', icon: Hash },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-blue-50/40 px-6 py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-lg font-semibold text-white shadow-sm"
                aria-hidden="true"
              >
                {getInitials(profile.firstName, profile.lastName)}
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold tracking-tight text-gray-900">{fullName}</h1>
                <p className="mt-1 truncate text-sm text-gray-600">{profile.email}</p>
                <span className="mt-2 inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                  {getRoleDisplayName(profile.role)}
                </span>
              </div>
            </div>
            {user.role !== 'admin' && user.role !== 'manager' ? (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 self-start rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </button>
            ) : null}
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Administra tu información personal y la seguridad de tu cuenta.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSaveProfile}
        className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 border-b border-gray-100 pb-4">
          <UserRound className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Información personal</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ProfileField
            label="Nombre"
            value={firstName}
            onChange={setFirstName}
            required
          />
          <ProfileField
            label="Apellido"
            value={lastName}
            onChange={setLastName}
            required
          />
          <ProfileField
            className="md:col-span-2"
            label="Teléfono"
            type="tel"
            value={phone}
            onChange={setPhone}
            placeholder="3001234567"
          />
          <ProfileField
            label="Correo electrónico"
            type="email"
            value={profile.email}
            readOnly
            icon={Mail}
          />
          <ProfileField
            label="Rol"
            value={getRoleDisplayName(profile.role)}
            readOnly
            icon={Shield}
          />
        </div>

        {showResidentialBlock ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <h3 className="text-sm font-semibold text-slate-800">Datos residenciales</h3>
            <p className="mt-1 text-xs text-slate-500">
              Administrados por la copropiedad. Para cambios, contacta al administrador.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {residentialItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="rounded-lg border border-white bg-white px-3 py-3 shadow-sm"
                  >
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      {item.label}
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-800">{item.value}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {showManagerBlock ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Building2 className="h-4 w-4 text-blue-600" />
              Conjunto asignado
            </div>
            <p className="mt-2 text-sm text-slate-800">{profile.managedComplexName || '—'}</p>
          </div>
        ) : null}

        {showBuilderBlock ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Building2 className="h-4 w-4 text-blue-600" />
              Conjuntos asignados
            </div>
            <p className="mt-2 text-sm text-slate-800">
              {(profile.assignedComplexes || []).map((item) => item.name).join(', ') || '—'}
            </p>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500">
            {isProfileDirty ? 'Tienes cambios sin guardar.' : 'Sin cambios pendientes.'}
          </p>
          <button
            type="submit"
            disabled={saving || !isProfileDirty}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Spinner size="sm" className="border-white" /> : <Save className="h-4 w-4" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      <form
        onSubmit={handleChangePassword}
        className="space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 border-b border-gray-100 pb-4">
          <KeyRound className="h-5 w-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Seguridad</h2>
            <p className="text-xs text-gray-500">Actualiza tu contraseña de acceso.</p>
          </div>
        </div>

        <div key={passwordFormKey} className="grid grid-cols-1 gap-4">
          <PasswordInput
            label="Contraseña actual"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            required={passwordsFilled}
          />
          <PasswordInput
            label="Nueva contraseña"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            minLength={8}
            helperText="Mínimo 8 caracteres."
            required={passwordsFilled}
            error={
              newPassword.length > 0 && !isNewPasswordValid
                ? 'La contraseña debe tener al menos 8 caracteres.'
                : undefined
            }
          />
          <PasswordInput
            label="Confirmar nueva contraseña"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            minLength={8}
            required={passwordsFilled}
            error={showConfirmMismatch ? 'Las contraseñas no coinciden.' : undefined}
          />
          {confirmPassword.length > 0 && passwordsMatch ? (
            <p className="text-sm text-emerald-600">Las contraseñas coinciden.</p>
          ) : null}
        </div>

        <div className="flex justify-end border-t border-gray-100 pt-4">
          <button
            type="submit"
            disabled={changingPassword || !canChangePassword}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-60',
              canChangePassword
                ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            )}
          >
            {changingPassword ? <Spinner size="sm" /> : <KeyRound className="h-4 w-4" />}
            {changingPassword ? 'Actualizando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </form>
    </div>
  );
};

const OwnerProfileShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6">
        <h1 className="text-lg font-semibold text-gray-900">Sistema de Asambleas</h1>
      </div>
    </div>
    <div className="px-4 py-8 sm:px-6">{children}</div>
  </div>
);

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return (
      <AdminLayout>
        <div className="p-4 pt-16 md:p-8 md:pt-8">
          <ProfilePageContent />
        </div>
      </AdminLayout>
    );
  }

  if (user?.role === 'manager') {
    return (
      <ManagerLayout>
        <div className="p-4 pt-16 md:p-8 md:pt-8">
          <ProfilePageContent />
        </div>
      </ManagerLayout>
    );
  }

  if (user?.role === 'builder') {
    return (
      <BuilderLayout>
        <div className="p-4 pt-16 md:p-8 md:pt-8">
          <ProfilePageContent />
        </div>
      </BuilderLayout>
    );
  }

  return (
    <OwnerProfileShell>
      <ProfilePageContent />
    </OwnerProfileShell>
  );
};

export default ProfilePage;
